<script setup>
import { computed, ref, watch, reactive, onMounted, nextTick } from 'vue'
import api from '@/services/api'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { filialSearch } from '@/search-definitions/filialSearch'
import { criarConfigBuscaLocalEstoque } from '@/search-definitions/localEstoqueSearch'
import { criarConfigBuscaDocumentoEntrada } from '@/search-definitions/documentoEntradaSearch'
import { useUI } from '@/composables/useUI'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { ESTQ_TIPO_LOCAL_ESTOCAGEM } from '@/constants/ESTQ_TIPO_LOCAL_ESTOCAGEM'
import { ESTQ_TIPO_LOCAL_ESTOCAGEM_CUSTOM } from '@/constants/ESTQ_TIPO_LOCAL_ESTOCAGEM.Custom'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  idProduto: { type: Number, default: null },
  idFilial: { type: Number, default: null },
  idLocalEstoque: { type: Number, default: null },
  idOrganizacao: { type: Number, default: null },
  lstTipoLocal: { type: String, default: null },
  idLoteFabricacao: { type: Number, default: null },
  // Âncoras de reserva existentes (para pré-seleção ao editar)
  idEmbarque: { type: Number, default: null },
  idEmbarqueContainer: { type: Number, default: null },
  idEmbarquePackingListProduto: { type: Number, default: null },
  idDocumentoEntrada: { type: Number, default: null },
  idDocumentoEntradaItem: { type: Number, default: null }
})

const emit = defineEmits(['update:modelValue', 'confirmar', 'show-notification'])

const { obterBadgeNeutro } = useUI()
const metadataStore = useMetadataListasStore()

const modelValueComputed = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Estado do produto selecionado
const idProdutoSelecionado = ref(props.idProduto)
const skuProduto = ref('')
const nomeProduto = ref('')
const unidadeMedidaBase = ref('')
const unidadeMedidaTexto = ref('')
const isServico = ref(false)

// Modo ativo (1=Pronta Entrega, 2=Trânsito [inclui Lotes via profundidade])
const abaAtiva = ref(1)
const abasCarregadas = reactive({ aba1: false, aba2: false })
const abasCarregando = reactive({ aba1: false, aba2: false })

// Dados de cada modo
const dadosAba1 = ref([])
const dadosAba2 = ref([])
const dadosAba2Filtrados = ref([])

// Filtros
const idFilialFiltro = ref(props.idFilial)
const idLocalEstoqueFiltro = ref(props.idLocalEstoque)
const lstTipoLocalFiltro = ref('')
const idDocumentoEntradaFiltro = ref(null)

// Guarda para não disparar a recarga durante a abertura do modal
const filtrosProntos = ref(false)

// Tipos de local de rastreabilidade (para os chips de filtro)
const tiposLocalTransito = [
  ESTQ_TIPO_LOCAL_ESTOCAGEM.PreEmbarque,
  ESTQ_TIPO_LOCAL_ESTOCAGEM.Transito,
  ESTQ_TIPO_LOCAL_ESTOCAGEM.AlfandegadoOuEntreposto
]

// ── PROFUNDIDADE DA ÁRVORE DE TRÂNSITO ──
// Definição dos níveis por profundidade. O DE é SEMPRE o último nível (nó folha
// selecionável). Os níveis anteriores são agregações (Local, Embarque, Container,
// PLP, Lote) que servem para navegação/expansão.
const PROFUNDIDADES = {
  de: {
    rotulo: 'DE',
    icone: 'bi-file-earmark-text',
    niveis: [
      { tipo: 'local' },
      { tipo: 'de' }
    ]
  },
  embarque: {
    rotulo: 'Embarque',
    icone: 'bi-truck',
    niveis: [
      { tipo: 'local' },
      { tipo: 'embarque' },
      { tipo: 'de' }
    ]
  },
  container: {
    rotulo: 'Container',
    icone: 'bi-box-seam',
    niveis: [
      { tipo: 'local' },
      { tipo: 'embarque' },
      { tipo: 'container' },
      { tipo: 'de' }
    ]
  },
  plp: {
    rotulo: 'PLP',
    icone: 'bi-file-earmark-zip',
    niveis: [
      { tipo: 'local' },
      { tipo: 'embarque' },
      { tipo: 'container' },
      { tipo: 'plp' },
      { tipo: 'de' }
    ]
  },
  lote: {
    rotulo: 'Lote',
    icone: 'bi-flask',
    niveis: [
      { tipo: 'lote' },
      { tipo: 'local' },
      { tipo: 'de' }
    ]
  }
}

const profundidadeAtiva = ref('embarque')

// Chips de profundidade disponíveis — contextuais ao tipo de local selecionado:
//   • Pré-Embarque: apenas DE (Filial → Local → DE, sem Embarque/Container/PLP).
//   • Trânsito/Alfandegado: Embarque, Container, PLP (sempre disponíveis).
const chipsProfundidade = computed(() => {
  const tipo = lstTipoLocalFiltro.value
  // Pré-Embarque: profundidade fixa Local → DE (sem nó Embarque).
  if (tipo === ESTQ_TIPO_LOCAL_ESTOCAGEM.PreEmbarque) {
    return [{ chave: 'de', rotulo: 'DE' }]
  }
  // Demais trânsitos: Embarque, Container, PLP sempre disponíveis.
  return [
    { chave: 'embarque', rotulo: 'Embarque' },
    { chave: 'container', rotulo: 'Container' },
    { chave: 'plp', rotulo: 'PLP' }
  ]
})

const profundidadeAtual = computed(() => PROFUNDIDADES[profundidadeAtiva.value] || PROFUNDIDADES.embarque)

// Enriquecimento dos dados de lote (número, validade, fabricação, fornecedor) via
// endpoint arvore-lotes — a árvore de trânsito retorna as âncoras mas não os dados do lote.
const loteInfoMap = ref({})

const carregarInfoLotes = async () => {
  if (!idProdutoSelecionado.value) {
    loteInfoMap.value = {}
    return
  }
  try {
    const params = new URLSearchParams()
    if (idFilialFiltro.value) params.append('idFilial', idFilialFiltro.value)
    if (idLocalEstoqueFiltro.value) params.append('idLocalEstoque', idLocalEstoqueFiltro.value)
    params.append('apenasDisponivel', 'true')
    const response = await api.get(`/SaldosEstoqueOrigem/arvore-lotes/${idProdutoSelecionado.value}?${params.toString()}`)
    const mapa = {}
    for (const l of response.data || []) {
      mapa[l.idSaldoEstoqueOrigem] = l
    }
    loteInfoMap.value = mapa
  } catch (e) {
    console.warn('Erro ao carregar informações de lotes:', e)
    loteInfoMap.value = {}
  }
}

const infoLoteDe = (item) => loteInfoMap.value[item?.idSaldoEstoqueOrigem] || {}
const getNumeroLote = (item) => item?.numeroLoteFornecedor || infoLoteDe(item).numeroLoteFornecedor || null
const getDataValidadeLote = (item) => infoLoteDe(item).dataValidade || null
const getDataFabricacaoLote = (item) => infoLoteDe(item).dataFabricacao || null

// Mapa código → rótulo do tipo de local (resolvido via Dicionário de Metadados)
const tiposLocalMap = ref({})

const carregarTiposLocal = async () => {
  try {
    const lista = await metadataStore.getListaPorCampo('LocalEstoque', 'lstTipoLocal')
    const mapa = {}
    for (const item of lista || []) {
      mapa[item.idListaClienteItem] = item.textoLista
    }
    tiposLocalMap.value = mapa
  } catch (e) {
    tiposLocalMap.value = {}
  }
}

// Rótulo do tipo de local (fallback para o próprio código)
const rotuloTipoLocal = (codigo) => tiposLocalMap.value[codigo] || codigo || ''

// Classe de cor do badge por natureza do tipo de local
const classeTipoLocal = (codigo) => {
  if (codigo === ESTQ_TIPO_LOCAL_ESTOCAGEM.PreEmbarque) return 'badge bg-warning text-dark'
  if (codigo === ESTQ_TIPO_LOCAL_ESTOCAGEM.Transito) return 'badge bg-info text-dark'
  if (codigo === ESTQ_TIPO_LOCAL_ESTOCAGEM.AlfandegadoOuEntreposto) return 'badge bg-secondary'
  if (ESTQ_TIPO_LOCAL_ESTOCAGEM_CUSTOM.ehEstoqueFinal(codigo)) return 'badge bg-success'
  return 'badge bg-secondary'
}

// ── SELEÇÃO (sem limite de origens) ──
// Cada item de seleção é uma ORIGEM completa = combinação de âncoras terminando
// em um DE (idDocumentoEntrada). Para Pronta Entrega o DE permanece null (o
// backend resolve via FIFO na efetivação da reserva).
const itensSelecionados = ref([])

// Campos de âncora (ordem canônica para comparação/prefixo).
const CAMPOS_ANCORA = [
  'idLocalEstoque',
  'idEmbarque',
  'idEmbarqueContainer',
  'idEmbarquePackingListProduto',
  'idLoteFabricacao',
  'idDocumentoEntrada',
  'idDocumentoEntradaItem'
]

// Chave estável de um conjunto de âncoras (para comparar seleção).
const chaveAncora = (ancoras) =>
  CAMPOS_ANCORA.map((c) => ancoras?.[c] ?? '').join('|')

// Normaliza um objeto de âncoras garantindo todas as chaves.
const ancorasDoNo = (ancoras) => {
  const resultado = {}
  for (const campo of CAMPOS_ANCORA) resultado[campo] = ancoras?.[campo] ?? null
  return resultado
}

const totalSelecionados = computed(() => itensSelecionados.value.length)

// Um conjunto de âncoras já está selecionado?
const isItemSelecionado = (ancoras) =>
  !!ancoras && itensSelecionados.value.some((s) => chaveAncora(s) === chaveAncora(ancoras))

// Alterna um DE na lista de selecionados.
const alternarSelecao = (ancoras, rotulo, quantidade, metadados = {}) => {
  const chave = chaveAncora(ancoras)
  const idx = itensSelecionados.value.findIndex((s) => chaveAncora(s) === chave)
  if (idx >= 0) {
    itensSelecionados.value.splice(idx, 1)
    return
  }
  itensSelecionados.value.push({
    ...ancorasDoNo(ancoras),
    rotulo: rotulo || '',
    quantidadeDisponivel: quantidade ?? 0,
    lstTipoLocal: metadados.lstTipoLocal ?? null,
    idFilial: metadados.idFilial ?? null,
    nomeFilial: metadados.nomeFilial ?? null,
    descricaoLocal: metadados.descricaoLocal || metadados.nomeLocalEstoque || '',
    nomeLocalEstoque: metadados.nomeLocalEstoque || metadados.descricaoLocal || '',
    numeroDocumentoEntrada: metadados.numeroDocumentoEntrada ?? null,
    numeroDocumentoTransporte: metadados.numeroDocumentoTransporte ?? null,
    numeroProcesso: metadados.numeroProcesso ?? null,
    numeroContainer: metadados.numeroContainer ?? null,
    lstTipoContainer: metadados.lstTipoContainer ?? null,
    numeroLoteFornecedor: metadados.numeroLoteFornecedor ?? null
  })
}

// Remove uma seleção pelo índice.
const removerSelecao = (idx) => {
  if (idx >= 0 && idx < itensSelecionados.value.length) {
    itensSelecionados.value.splice(idx, 1)
  }
}

// Limpa a seleção.
const limparSelecao = () => {
  itensSelecionados.value = []
}

// ── PRÉ-SELEÇÃO DE ÂNCORAS EXISTENTES ──
// Quando o modal é aberto para editar um item que já possui âncoras de reserva
// (ex: idEmbarque, idDocumentoEntrada), estas são pré-selecionadas na tabela.
// A pré-seleção ocorre após o carregamento dos dados e montagem da árvore.
const ancorasExistentes = computed(() => {
  const a = {}
  a.idLocalEstoque = props.idLocalEstoque ?? null
  a.idEmbarque = props.idEmbarque ?? null
  a.idEmbarqueContainer = props.idEmbarqueContainer ?? null
  a.idEmbarquePackingListProduto = props.idEmbarquePackingListProduto ?? null
  a.idLoteFabricacao = props.idLoteFabricacao ?? null
  a.idDocumentoEntrada = props.idDocumentoEntrada ?? null
  a.idDocumentoEntradaItem = props.idDocumentoEntradaItem ?? null
  return a
})

const temAncorasExistentes = computed(() =>
  CAMPOS_ANCORA.some(c => ancorasExistentes.value[c] != null)
)

// Tenta pré-selecionar as âncoras existentes na árvore montada ou nos cards
// de Pronta Entrega. Chamado sempre que a árvore/cards são carregados e há
// âncoras para pré-selecionar que ainda não foram selecionadas.
const tentarPreSelecionarAncoras = () => {
  if (!temAncorasExistentes.value) return
  const ancoras = ancorasExistentes.value
  // Já selecionado? Não refaz.
  if (isItemSelecionado(ancoras)) return

  // Caso 1: Pronta Entrega (apenas idLocalEstoque, sem DE/embarque/lote)
  if (ancoras.idLocalEstoque != null &&
      ancoras.idEmbarque == null &&
      ancoras.idEmbarqueContainer == null &&
      ancoras.idEmbarquePackingListProduto == null &&
      ancoras.idLoteFabricacao == null &&
      ancoras.idDocumentoEntrada == null) {
    // Procura o card de Pronta Entrega que corresponde ao local.
    const card = dadosAba1.value.find(l => l.idLocalEstoque === ancoras.idLocalEstoque)
    if (card) {
      // Muda para aba 1 se necessário
      if (abaAtiva.value !== 1) {
        abaAtiva.value = 1
      }
      selecionarCardProntaEntrega(card)
    }
    return
  }

  // Caso 2: Trânsito (com âncoras de rastreio) — procura o nó DE na árvore.
  const deNode = linhasArvore.value.find(l =>
    l.data.ehDocumentoEntrada &&
    CAMPOS_ANCORA.every(c => ancoras[c] == null || l.data.ancoras[c] === ancoras[c])
  )
  if (deNode) {
    alternarSelecao(
      deNode.data.ancoras,
      deNode.data.rotuloSelecao,
      deNode.data.quantidadeDisponivel,
      deNode.data
    )
  }
}

// ── MONTAGEM GENÉRICA DA ÁRVORE POR PROFUNDIDADE ──
// Chave de agrupamento de um item bruto para um nível.
const chaveDoNivel = (item, tipo) => {
  switch (tipo) {
    case 'local': return `local-${item.idFilial ?? 0}-${item.idLocalEstoque ?? 0}`
    case 'embarque': return item.idEmbarque != null ? `emb-${item.idEmbarque}` : 'emb-sem'
    case 'container': return item.idEmbarqueContainer != null ? `cont-${item.idEmbarqueContainer}` : 'cont-sem'
    case 'plp': return item.idEmbarquePackingListProduto != null ? `plp-${item.idEmbarquePackingListProduto}` : 'plp-sem'
    case 'lote': return item.idLoteFabricacao != null ? `lote-${item.idLoteFabricacao}` : 'lote-sem'
    case 'de': return item.idDocumentoEntrada != null ? `de-${item.idDocumentoEntrada}` : 'de-sem'
    default: return 'x'
  }
}

// Rótulo de um nó bruto para um nível.
const rotuloDoNivel = (item, tipo) => {
  switch (tipo) {
    case 'local':
      return `${item.nomeFilial || ''} — ${item.nomeLocalEstoque || ''}`.trim() || 'Local'
    case 'embarque':
      return item.idEmbarque != null
        ? `Embarque ${item.numeroDocumentoTransporte || '#' + item.idEmbarque}`
        : 'Sem Embarque'
    case 'container':
      return item.idEmbarqueContainer != null
        ? `Container ${item.numeroContainer || '#' + item.idEmbarqueContainer}`
        : 'Sem Container'
    case 'plp':
      return item.idEmbarquePackingListProduto != null
        ? `PLP #${item.idEmbarquePackingListProduto}`
        : 'Sem PLP'
    case 'lote':
      return item.idLoteFabricacao != null
        ? `Lote ${getNumeroLote(item) || '#' + item.idLoteFabricacao}`
        : 'Sem Lote'
    case 'de':
      return item.idDocumentoEntrada != null
        ? `DE ${item.numeroDocumentoEntrada || '#' + item.idDocumentoEntrada}`
        : 'Sem Documento de Entrada'
    default:
      return ''
  }
}

// Monta o objeto `data` de um nó: âncoras acumuladas até o nível + metadados de exibição.
const montarDataDoNivel = (item, idxNivel, config, quantidade) => {
  const ancoras = {
    idLocalEstoque: null,
    idEmbarque: null,
    idEmbarqueContainer: null,
    idEmbarquePackingListProduto: null,
    idLoteFabricacao: null,
    idDocumentoEntrada: null,
    idDocumentoEntradaItem: null
  }

  for (let i = 0; i <= idxNivel; i++) {
    const n = config.niveis[i]
    if (n.tipo === 'local') {
      ancoras.idLocalEstoque = item.idLocalEstoque ?? null
    } else if (n.tipo === 'embarque') {
      ancoras.idEmbarque = item.idEmbarque ?? null
    } else if (n.tipo === 'container') {
      ancoras.idEmbarqueContainer = item.idEmbarqueContainer ?? null
    } else if (n.tipo === 'plp') {
      ancoras.idEmbarquePackingListProduto = item.idEmbarquePackingListProduto ?? null
    } else if (n.tipo === 'lote') {
      ancoras.idLoteFabricacao = item.idLoteFabricacao ?? null
    } else if (n.tipo === 'de') {
      ancoras.idDocumentoEntrada = item.idDocumentoEntrada ?? null
      ancoras.idDocumentoEntradaItem = item.idDocumentoEntradaItem ?? null
    }
  }

  const tipoNivel = config.niveis[idxNivel].tipo
  const ehUltimo = idxNivel === config.niveis.length - 1

  return {
    ancoras,
    tipo: tipoNivel,
    ehDocumentoEntrada: ehUltimo,
    chave: null, // preenchido pelo montador
    rotuloSelecao: rotuloDoNivel(item, tipoNivel),
    quantidadeDisponivel: quantidade,
    nomeFilial: item.nomeFilial ?? null,
    idFilial: item.idFilial ?? null,
    nomeLocalEstoque: item.nomeLocalEstoque ?? null,
    descricaoLocal: item.nomeLocalEstoque ?? '',
    lstTipoLocal: item.lstTipoLocal ?? null,
    numeroDocumentoEntrada: item.numeroDocumentoEntrada ?? null,
    numeroDocumentoTransporte: item.numeroDocumentoTransporte ?? null,
    numeroProcesso: item.numeroProcesso ?? null,
    numeroContainer: item.numeroContainer ?? null,
    lstTipoContainer: item.lstTipoContainer ?? null,
    numeroLoteFornecedor: getNumeroLote(item),
    dataValidade: getDataValidadeLote(item),
    dataFabricacao: getDataFabricacaoLote(item),
    nomeFornecedor: item.nomeFornecedor ?? infoLoteDe(item).nomeFornecedor ?? null
  }
}

// Monta a árvore plana de linhas conforme a profundidade ativa.
// O último nível é SEMPRE o DE (nó folha). Nós intermediários servem para
// navegação e para seleção em massa (todos os DEs descendentes).
const montarArvorePorProfundidade = (itens, prof) => {
  const config = PROFUNDIDADES[prof] || PROFUNDIDADES.embarque
  const linhas = []
  const dados = (itens || []).filter(i => Number(i.quantidadeDisponivel) > 0)
  if (dados.length === 0) return linhas

  const agrupar = (grupo, idxNivel, parentChave, ancestrais) => {
    const nivel = config.niveis[idxNivel]
    const ehUltimo = idxNivel === config.niveis.length - 1

    const mapa = new Map()
    for (const item of grupo) {
      const chave = chaveDoNivel(item, nivel.tipo)
      if (!mapa.has(chave)) mapa.set(chave, { itens: [], ref: item, quantidade: 0 })
      const g = mapa.get(chave)
      g.itens.push(item)
      g.quantidade += Number(item.quantidadeDisponivel) || 0
    }

    for (const [chave, g] of mapa) {
      const chaveCompleta = parentChave ? `${parentChave}/${chave}` : chave
      const data = montarDataDoNivel(g.ref, idxNivel, config, g.quantidade)
      data.chave = chaveCompleta
      linhas.push({
        chave: chaveCompleta,
        level: idxNivel + 1,
        parentKey: parentChave || null,
        ancestraisRequeridos: ancestrais,
        isParent: !ehUltimo,
        data
      })
      if (!ehUltimo) {
        agrupar(g.itens, idxNivel + 1, chaveCompleta, [...ancestrais, parentChave].filter(Boolean))
      }
    }
  }

  agrupar(dados, 0, null, [])
  return linhas
}

// Estado de expansão da árvore de Trânsito (chaves expandidas), por profundidade.
const transitoExpandido = reactive({})

// Visibilidade de uma linha: todos os ancestrais expandidos.
const linhaTransitoVisivel = (linha) =>
  (linha.ancestraisRequeridos || []).every((k) => !!transitoExpandido[k])

// Toggle expandir/recolher um nó.
const toggleNivel = (chave) => {
  transitoExpandido[chave] = !transitoExpandido[chave]
}

// Expande todos os nós-pai.
const expandirTudo = () => {
  const pais = new Set(linhasArvore.value.map(l => l.parentKey).filter(Boolean))
  for (const chave of pais) transitoExpandido[chave] = true
}

// Recolhe todos os nós.
const recolherTudo = () => {
  Object.keys(transitoExpandido).forEach((key) => delete transitoExpandido[key])
}

// Computado que monta a árvore conforme a profundidade ativa + filtro de DE.
const linhasArvore = computed(() => {
  const dados = dadosAba2Filtrados.value
  if (!dados || dados.length === 0) return []
  return montarArvorePorProfundidade(dados, profundidadeAtiva.value)
})

// Ícone semântico por nível.
const iconeDoNivel = (data) => {
  switch (data?.tipo) {
    case 'local': return 'bi-building'
    case 'embarque': return 'bi-truck'
    case 'container': return 'bi-box-seam'
    case 'plp': return 'bi-file-earmark-zip'
    case 'lote': return 'bi-flask'
    case 'de': return 'bi-file-earmark-text'
    default: return 'bi-dot'
  }
}

// Um nó está selecionado quando suas âncoras batem exatamente com uma seleção.
const isNoSelecionado = (linha) => isItemSelecionado(linha.data.ancoras)

// Um nó é ANCESTRAL de uma seleção quando compartilha todas as âncoras não nulas
// de uma delas (destaque dos ancestrais na árvore) e não é o próprio DE.
const isAncestralSelecionado = (linha) => {
  const ancoras = linha.data.ancoras
  return itensSelecionados.value.some((sel) => {
    if (!sel) return false
    if (chaveAncora(sel) === chaveAncora(ancoras)) return false
    return CAMPOS_ANCORA.every((c) => sel[c] == null || sel[c] === (ancoras?.[c] ?? null))
  })
}

// Classe de destaque das linhas da árvore.
const rowClassArvore = (linha) => {
  if (isNoSelecionado(linha)) return 'linha-selecionada fw-semibold'
  if (isAncestralSelecionado(linha)) return 'linha-filha-selecionada'
  return ''
}

// Coleta todos os nós DE descendentes de um nó pai (seleção em massa).
// Um DE é descendente quando a chave do pai está entre seus ancestraisRequeridos.
const desDescendentesDe = (chavePai) =>
  linhasArvore.value.filter((l) => l.data.ehDocumentoEntrada && l.ancestraisRequeridos.includes(chavePai))

// Seleciona/desseleciona um nó da árvore:
//   • nó DE (folha) → alterna a seleção individual.
//   • nó intermediário → seleção em massa: adiciona/remove TODOS os DEs descendentes.
const selecionarNo = (linha) => {
  if (!linha?.data) return
  if (linha.data.ehDocumentoEntrada) {
    alternarSelecao(
      linha.data.ancoras,
      linha.data.rotuloSelecao,
      linha.data.quantidadeDisponivel,
      linha.data
    )
    return
  }
  // Seleção em massa: coletar os DEs descendentes.
  const des = desDescendentesDe(linha.data.chave)
  if (des.length === 0) return
  const todosSelecionados = des.every(d => isItemSelecionado(d.data.ancoras))
  if (todosSelecionados) {
    // Remove todos os DEs descendentes.
    const chaves = new Set(des.map(d => chaveAncora(d.data.ancoras)))
    itensSelecionados.value = itensSelecionados.value.filter(s => !chaves.has(chaveAncora(s)))
  } else {
    // Adiciona todos os DEs descendentes.
    for (const d of des) {
      alternarSelecao(
        d.data.ancoras,
        d.data.rotuloSelecao,
        d.data.quantidadeDisponivel,
        d.data
      )
    }
  }
}

// Rótulo combinado para a exibição na tabela de seleção (DE + caminho).
const rotuloSelecaoCompleto = (sel) => {
  const partes = []
  if (sel.nomeLocalEstoque) partes.push(sel.nomeLocalEstoque)
  if (sel.idEmbarque != null) partes.push(`Emb #${sel.idEmbarque}`)
  if (sel.idEmbarqueContainer != null) partes.push(`Cont #${sel.idEmbarqueContainer}`)
  if (sel.idEmbarquePackingListProduto != null) partes.push(`PLP #${sel.idEmbarquePackingListProduto}`)
  if (sel.idLoteFabricacao != null) partes.push(`Lote #${sel.idLoteFabricacao}`)
  if (sel.idDocumentoEntrada != null) partes.push(sel.numeroDocumentoEntrada || `DE #${sel.idDocumentoEntrada}`)
  return partes.join(' · ')
}

// Seleciona/desmarca um card da Pronta Entrega — apenas o local (sem DE).
const selecionarCardProntaEntrega = (row) => {
  if (!row) return
  alternarSelecao(
    { idLocalEstoque: row.idLocalEstoque },
    `${row.nomeFilial || ''} — ${row.nomeLocalEstoque || ''}`.trim(),
    row.quantidadeDisponivel ?? 0,
    {
      lstTipoLocal: row.lstTipoLocal ?? null,
      idFilial: row.idFilial ?? null,
      nomeFilial: row.nomeFilial ?? null,
      nomeLocalEstoque: row.nomeLocalEstoque || ''
    }
  )
}

// Card de armazém selecionado (Pronta Entrega).
const isCardSelecionado = (row) =>
  itensSelecionados.value.some((s) => s.idLocalEstoque === row.idLocalEstoque && s.idEmbarque == null)

// Configurações de busca
const configBuscaProduto = computed(() =>
  criarConfigBuscaProduto()
)

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial'
}))

const configBuscaLocalEstoque = computed(() =>
  criarConfigBuscaLocalEstoque(props.idOrganizacao, idFilialFiltro.value)
)

const configBuscaDocumentoEntrada = computed(() =>
  criarConfigBuscaDocumentoEntrada(props.idOrganizacao)
)

// Há algum filtro ativo (filial, local, tipo de local, documento de entrada)?
const temFiltroAtivo = computed(() =>
  !!idFilialFiltro.value ||
  !!idLocalEstoqueFiltro.value ||
  !!lstTipoLocalFiltro.value ||
  !!idDocumentoEntradaFiltro.value
)

// Filtra a árvore de trânsito por tipo de local.
const filtrarPorTipo = (codigo) => {
  lstTipoLocalFiltro.value = codigo
}

// Limpa todos os filtros e recarrega o modo ativo.
const limparFiltros = () => {
  idFilialFiltro.value = null
  idLocalEstoqueFiltro.value = null
  lstTipoLocalFiltro.value = ''
  idDocumentoEntradaFiltro.value = null
}

// Título dinâmico do modal
const tituloModal = computed(() => {
  const prefixo = skuProduto.value ? `${skuProduto.value} - ${nomeProduto.value}` : 'Produto'
  return `🏷️ Selecionar Estoque — ${prefixo}`
})

// Carregar produto por ID
const carregarProdutoPorId = async (id) => {
  try {
    const response = await api.get(`/Produtos/${id}`)
    idProdutoSelecionado.value = response.data.idProduto
    skuProduto.value = response.data.sku
    nomeProduto.value = response.data.nome
    unidadeMedidaBase.value = response.data.lstUnidadeMedidaBase || ''
    // Detecta se o produto é um serviço (sem controle de estoque)
    isServico.value = response.data.isServico === true
    // Resolve o texto amigável da unidade de medida via dicionário
    if (unidadeMedidaBase.value) {
      const lista = await metadataStore.getListaPorCampo('Produto', 'lstUnidadeMedidaBase')
      const item = (lista || []).find(l => String(l.idListaClienteItem) === String(unidadeMedidaBase.value))
      unidadeMedidaTexto.value = item?.textoLista || unidadeMedidaBase.value
    } else {
      unidadeMedidaTexto.value = ''
    }
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || 'Erro ao carregar produto.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// Manipular seleção de produto no CampoBusca
const handleProdutoSelecionado = (produto) => {
  if (produto) {
    idProdutoSelecionado.value = produto.idProduto
    skuProduto.value = produto.sku
    nomeProduto.value = produto.nome
    // Se o backend já retornou isServico no objeto do produto, aproveita
    if (produto.isServico !== undefined) {
      isServico.value = produto.isServico === true
    }
  }
}

// Resolve o modo inicial do modal conforme o contexto recebido:
//   idLoteFabricacao → Trânsito com profundidade Lote;
//   local de trânsito → Trânsito (profundidade padrão Embarque);
//   senão → Pronta Entrega.
const resolverModoInicial = async () => {
  if (props.idLoteFabricacao) return { aba: 2, profundidade: 'lote' }
  let tipo = props.lstTipoLocal
  if (!tipo && props.idLocalEstoque) {
    try {
      const response = await api.get(`/LocaisEstoque/${props.idLocalEstoque}`)
      tipo = response.data?.lstTipoLocal ?? null
    } catch (e) {
      tipo = null
    }
  }
  if (tipo && ESTQ_TIPO_LOCAL_ESTOCAGEM_CUSTOM.ehEstoqueTransito(tipo)) {
    return { aba: 2, profundidade: 'embarque' }
  }
  return { aba: 1, profundidade: 'embarque' }
}

// Função para formatar quantidade com unidade de medida (ex: "85 CX")
const formatarQtd = (qtd) => {
  const num = Number(qtd || 0)
  const qtdStr = num === Math.floor(num) ? num.toString() : num.toFixed(2).replace('.', ',')
  const unid = unidadeMedidaTexto.value
  return unid ? `${qtdStr} ${unid}` : qtdStr
}

// Função para carregar Pronta Entrega
const carregarAba1 = async () => {
  if (!idProdutoSelecionado.value) return
  if (abasCarregadas.aba1 || abasCarregando.aba1) return

  abasCarregando.aba1 = true
  try {
    let url = `/SaldosEstoque?idsProdutos=${idProdutoSelecionado.value}&apenasDisponivel=true&ocultarSaldoZero=true&apenasLocaisLiberadosReserva=true&apenasLocaisProntaEntrega=true`

    if (idFilialFiltro.value) {
      url += `&idFilial=${idFilialFiltro.value}`
    }
    if (idLocalEstoqueFiltro.value) {
      url += `&idLocalEstoque=${idLocalEstoqueFiltro.value}`
    }

    const response = await api.get(url, { _suppressLoading: true })
    dadosAba1.value = response.data || []
    abasCarregadas.aba1 = true
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || 'Erro ao carregar saldos de estoque.'
    emit('show-notification', { message: msg, type: 'error' })
    dadosAba1.value = []
  } finally {
    abasCarregando.aba1 = false
  }
}

// Função para carregar Material em Trânsito (fonte base com todas as âncoras + DE)
const carregarAba2 = async () => {
  if (!idProdutoSelecionado.value || abasCarregando.aba2) return
  abasCarregando.aba2 = true
  try {
    const params = new URLSearchParams()
    if (idFilialFiltro.value) params.append('idFilial', idFilialFiltro.value)
    if (idLocalEstoqueFiltro.value) params.append('idLocalEstoque', idLocalEstoqueFiltro.value)
    if (lstTipoLocalFiltro.value) params.append('lstTipoLocal', lstTipoLocalFiltro.value)
    params.append('apenasDisponivel', 'true')

    const response = await api.get(`/SaldosEstoqueOrigem/arvore-transito/${idProdutoSelecionado.value}?${params.toString()}`)
    dadosAba2.value = response.data || []
    aplicarFiltrosAba2()
    abasCarregadas.aba2 = true
    // Enriquecimento de lotes (em background, sem travar a árvore)
    carregarInfoLotes()
  } catch (e) {
    console.error('Erro ao carregar Material em Trânsito:', e)
    emit('show-notification', { message: 'Erro ao carregar dados em trânsito.', type: 'error' })
  } finally {
    abasCarregando.aba2 = false
  }
}

// ── Filtro client-side de Documento de Entrada (aba Trânsito) ──
const handleDocumentoEntradaSelecionado = (de) => {
  idDocumentoEntradaFiltro.value = de?.idDocumentoEntrada ?? null
}

const aplicarFiltrosAba2 = () => {
  let lista = [...dadosAba2.value]
  if (idDocumentoEntradaFiltro.value) {
    lista = lista.filter((i) => i.idDocumentoEntrada === idDocumentoEntradaFiltro.value)
  }
  dadosAba2Filtrados.value = lista
}

// Aviso quando o filtro de DE não encontra saldo no produto/filtros atuais.
const deFiltroSemResultado = computed(() =>
  !!idDocumentoEntradaFiltro.value &&
  dadosAba2.value.length > 0 &&
  dadosAba2Filtrados.value.length === 0
)

// Recarregar Pronta Entrega quando filtros mudam
const recarregarAba1 = async () => {
  abasCarregadas.aba1 = false
  await carregarAba1()
}

// Recarregar Trânsito quando filtros mudam
const recarregarAba2 = async () => {
  abasCarregadas.aba2 = false
  await carregarAba2()
}

const recarregarAbaAtiva = () => {
  if (abaAtiva.value === 1) recarregarAba1()
  else recarregarAba2()
}

// ── Proteção de troca de aba (seleções são perdidas) ──
const confirmacaoTrocaAbaRef = ref(null)
const abaDestino = ref(null)

// Ativar modo com lazy loading. Se houver seleções na aba atual, exibe
// confirmação antes de trocar (as seleções são perdidas).
const ativarAba = (numero) => {
  if (numero === abaAtiva.value) return
  if (itensSelecionados.value.length > 0) {
    abaDestino.value = numero
    if (confirmacaoTrocaAbaRef.value) {
      confirmacaoTrocaAbaRef.value.show()
    }
    return
  }
  executarTrocaAba(numero)
}

const executarTrocaAba = (numero) => {
  abaAtiva.value = numero
  if (numero === 1 && !abasCarregadas.aba1 && !abasCarregando.aba1) {
    carregarAba1()
  }
  if (numero === 2 && !abasCarregadas.aba2 && !abasCarregando.aba2) {
    carregarAba2()
  }
}

const onConfirmTrocaAba = (confirmed) => {
  if (confirmed && abaDestino.value != null) {
    limparSelecao()
    executarTrocaAba(abaDestino.value)
  }
  abaDestino.value = null
}

// Confirmar seleção — emite a lista completa de origens selecionadas.
const _confirmarSelecao = () => {
  if (itensSelecionados.value.length === 0 && !isServico.value) return
  emit('confirmar', {
    idProduto: idProdutoSelecionado.value,
    sku: skuProduto.value,
    nome: nomeProduto.value,
    isServico: isServico.value,
    itens: itensSelecionados.value.map((s) => ({ ...s }))
  })
  modelValueComputed.value = false
}

// Cancelar e fechar
const _cancelar = () => {
  modelValueComputed.value = false
}

// Watch para abrir modal e carregar produto automaticamente
watch(() => props.modelValue, async (novoValor) => {
  if (novoValor) {
    filtrosProntos.value = false
    const modo = await resolverModoInicial()
    abaAtiva.value = modo.aba
    profundidadeAtiva.value = modo.profundidade
    abasCarregadas.aba1 = false
    abasCarregadas.aba2 = false
    abasCarregando.aba1 = false
    abasCarregando.aba2 = false
    dadosAba1.value = []
    dadosAba2.value = []
    dadosAba2Filtrados.value = []
    idDocumentoEntradaFiltro.value = null
    loteInfoMap.value = {}
    itensSelecionados.value = []
    Object.keys(transitoExpandido).forEach(key => delete transitoExpandido[key])
    idFilialFiltro.value = props.idFilial
    idLocalEstoqueFiltro.value = props.idLocalEstoque

    if (props.idProduto) {
      await carregarProdutoPorId(props.idProduto)
    }

    await nextTick()
    filtrosProntos.value = true

    // Carrega a aba ativa + a outra em background (lazy)
    if (abaAtiva.value === 2) {
      carregarAba2()
      carregarAba1()
    } else {
      carregarAba1()
      carregarAba2()
    }
  }
})

// Watch para idProdutoSelecionado: recarrega todas as abas quando produto muda
watch(idProdutoSelecionado, async (novoId) => {
  if (!novoId) return
  abasCarregadas.aba1 = false
  abasCarregadas.aba2 = false
  dadosAba2Filtrados.value = []
  idDocumentoEntradaFiltro.value = null
  loteInfoMap.value = {}
  itensSelecionados.value = []
  Object.keys(transitoExpandido).forEach(key => delete transitoExpandido[key])
  await carregarAba1()
  carregarAba2()
})

// Watch dos filtros filial/local/tipo de local (server-side)
watch([idFilialFiltro, idLocalEstoqueFiltro, lstTipoLocalFiltro], () => {
  if (!filtrosProntos.value) return
  // Ao trocar o tipo de local, reseta a profundidade para o padrão do tipo.
  if (lstTipoLocalFiltro.value === ESTQ_TIPO_LOCAL_ESTOCAGEM.PreEmbarque) {
    profundidadeAtiva.value = 'embarque'
  }
  recarregarAbaAtiva()
})

// Watch do filtro de Documento de Entrada: aplicação client-side imediata (sem recarga).
watch(idDocumentoEntradaFiltro, () => {
  if (!filtrosProntos.value) return
  aplicarFiltrosAba2()
})

// Ao trocar a profundidade, limpa o estado de expansão e expande tudo.
watch(profundidadeAtiva, () => {
  Object.keys(transitoExpandido).forEach(key => delete transitoExpandido[key])
  nextTick(() => expandirTudo())
})

// Auto-expande a árvore sempre que ela for remontada (carga inicial, troca de
// filtro, troca de profundidade). O usuário pode recolher níveis depois — o
// estado de expansão é independente de remontagens subsequentes.
watch(linhasArvore, (novas) => {
  if (!Array.isArray(novas) || novas.length === 0) return
  nextTick(() => expandirTudo())
  // Tenta pré-selecionar as âncoras existentes (se houver).
  nextTick(() => tentarPreSelecionarAncoras())
})

// Quando os cards de Pronta Entrega são carregados, tenta pré-selecionar
// as âncoras existentes (apenas idLocalEstoque, sem DE).
watch(dadosAba1, () => {
  nextTick(() => tentarPreSelecionarAncoras())
})

onMounted(async () => {
  await carregarTiposLocal()
  // Expande todos os nós ao montar a árvore inicial (se já houver dados).
  nextTick(() => expandirTudo())
})
</script>

<template>
  <ModalFormularioV2
    v-model="modelValueComputed"
    :titulo="tituloModal"
    tamanho="xl"
    cor-header="var(--bs-primary)"
  >
    <!-- Seletor de modo (topo) — oculto para serviços -->
    <template v-if="!isServico">
      <ul class="nav nav-tabs mb-3" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: abaAtiva === 1 }"
            type="button"
            role="tab"
            @click="ativarAba(1)"
          >
            <i class="bi bi-box-seam me-1"></i> Pronta Entrega
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: abaAtiva === 2 }"
            type="button"
            role="tab"
            @click="ativarAba(2)"
          >
            <i class="bi bi-truck me-1"></i> Trânsito
          </button>
        </li>
      </ul>
    </template>

    <!-- Linha de filtros -->
    <div class="row g-2 align-items-start mb-1">
      <div class="col-6 col-md-3">
        <CampoBusca
          id="produtoSelecao"
          label="Produto"
          v-model="idProdutoSelecionado"
          :configuracao-busca="configBuscaProduto"
          @selecionado="handleProdutoSelecionado"
        />
      </div>
      <div class="col-6 col-md-3">
        <CampoBusca
          id="filtroFilial"
          label="Filial"
          v-model="idFilialFiltro"
          :configuracao-busca="configBuscaFilial"
        />
      </div>
      <div class="col-6 col-md-3">
        <CampoBusca
          id="filtroLocalEstoque"
          label="Local Estoque"
          v-model="idLocalEstoqueFiltro"
          :configuracao-busca="configBuscaLocalEstoque"
        />
      </div>
      <!-- Filtro de Documento de Entrada (somente na aba Trânsito) -->
      <div class="col-6 col-md-3">
        <CampoBusca
          v-if="abaAtiva === 2"
          id="filtroDocumentoEntrada"
          label="Documento de Entrada"
          v-model="idDocumentoEntradaFiltro"
          :configuracao-busca="configBuscaDocumentoEntrada"
          @selecionado="handleDocumentoEntradaSelecionado"
        />
      </div>
      <div class="col-12 col-md-auto d-flex align-items-end">
        <div class="form-label invisible d-none d-md-block">&nbsp;</div>
        <button
          v-if="temFiltroAtivo"
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="limparFiltros"
        >
          <i class="bi bi-x-circle me-1"></i> Limpar filtros
        </button>
      </div>
    </div>

    <!-- Área de seleção demarcada -->
    <div class="tab-content area-selecao">
      <!-- Modo Serviço — sem estoque, apenas confirmação -->
      <div v-if="isServico" class="text-center py-4">
        <i class="bi bi-tools fs-1 d-block mb-2 text-primary"></i>
        <h5 class="fw-semibold mb-2">{{ skuProduto }} — {{ nomeProduto }}</h5>
        <p class="text-body-secondary mb-0">
          Este produto é um <strong>serviço</strong> e não possui controle de estoque.
        </p>
        <p class="text-body-secondary">
          Clique em <strong>Selecionar</strong> para confirmar a inclusão no pedido.
        </p>
      </div>

      <!-- Pronta Entrega — cards de armazém -->
      <div v-else-if="abaAtiva === 1">
        <div v-if="abasCarregando.aba1" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
          <span class="text-body-secondary">Carregando saldos...</span>
        </div>
        <div v-else-if="dadosAba1.length === 0" class="alert alert-secondary text-center">
          <i class="bi bi-info-circle me-1"></i>
          Nenhum saldo encontrado para o produto selecionado.
        </div>
        <div v-else class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
          <div v-for="(local, idx) in dadosAba1" :key="'local-' + idx" class="col">
            <div
              class="card h-100 card-armazem"
              :class="{ 'card-armazem-selecionado': isCardSelecionado(local) }"
              @click="selecionarCardProntaEntrega(local)"
            >
              <div class="card-body">
                <div class="d-flex align-items-start gap-2">
                  <i class="bi bi-building fs-3 text-primary"></i>
                  <div class="flex-grow-1">
                    <div class="fw-bold">{{ local.nomeLocalEstoque }}</div>
                    <span class="bloco-filial mt-1">
                      <i class="bi bi-geo-alt me-1"></i>{{ local.nomeFilial }}
                      <code class="ms-1">#{{ local.idFilial }}</code>
                    </span>
                    <div v-if="local.lstTipoLocal" class="mt-1">
                      <span :class="classeTipoLocal(local.lstTipoLocal)">
                        {{ rotuloTipoLocal(local.lstTipoLocal) }}
                      </span>
                    </div>
                  </div>
                  <i v-if="isCardSelecionado(local)" class="bi bi-check-circle-fill text-primary fs-5"></i>
                </div>
                <hr class="my-2">
                <div class="d-flex justify-content-between small">
                  <span class="text-body-secondary">Em estoque</span>
                  <span>{{ formatarQtd(local.quantidadeEmEstoque) }}</span>
                </div>
                <div class="d-flex justify-content-between">
                  <span class="text-body-secondary">Disponível</span>
                  <span class="fw-bold text-success">{{ formatarQtd(local.quantidadeDisponivel) }}</span>
                </div>
                <div class="d-flex justify-content-between small">
                  <span class="text-body-secondary">Reservada</span>
                  <span>{{ formatarQtd(local.quantidadeReservada) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Material em Trânsito — árvore dinâmica por profundidade -->
      <div v-else-if="abaAtiva === 2">
        <!-- Filtro por tipo de local (Pré-Embarque, Trânsito, Alfandegado) -->
        <div class="d-flex flex-wrap gap-2 mb-1">
          <button
            v-for="tipo in tiposLocalTransito"
            :key="tipo"
            type="button"
            class="btn btn-sm"
            :class="lstTipoLocalFiltro === tipo ? 'btn-primary' : 'btn-outline-secondary'"
            @click="filtrarPorTipo(tipo)"
          >
            {{ rotuloTipoLocal(tipo) }}
          </button>
        </div>

        <!-- Chips de PROFUNDIDADE (qual o último nível de agregação antes do DE) -->
        <div class="d-flex flex-wrap gap-2 mb-2">
          <button
            v-for="chip in chipsProfundidade"
            :key="chip.chave"
            type="button"
            class="btn btn-sm"
            :class="profundidadeAtiva === chip.chave ? 'btn-primary' : 'btn-outline-secondary'"
            @click="profundidadeAtiva = chip.chave"
          >
            <i class="bi me-1" :class="PROFUNDIDADES[chip.chave]?.icone"></i>
            {{ chip.rotulo }}
          </button>
        </div>

        <div class="d-flex justify-content-between align-items-center mb-2">
          <small class="text-body-secondary">
            <i class="bi bi-info-circle me-1"></i>
            Selecione o <b>Documento de Entrada (DE)</b> de cada origem. Clique em um nó pai
            para selecionar <b>todos</b> os DE abaixo dele.
          </small>
          <div class="d-flex gap-2">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="expandirTudo">
              <i class="bi bi-arrows-expand me-1"></i> Expandir
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="recolherTudo">
              <i class="bi bi-arrows-collapse me-1"></i> Recolher
            </button>
          </div>
        </div>

        <div v-if="deFiltroSemResultado" class="alert alert-warning py-2 small mb-2">
          <i class="bi bi-exclamation-triangle me-1"></i>
          Nenhum saldo para o documento de entrada buscado com os filtros atuais.
        </div>

        <div v-if="linhasArvore.length > 0" class="table-responsive" style="max-height: 400px; overflow-y: auto;">
          <table class="table table-sm table-hover admin-table mb-0 arvore-tabela">
            <thead class="sticky-top">
              <tr>
                <th style="width: 40px;"></th>
                <th style="width: 40px;"></th>
                <th>{{ profundidadeAtual.rotulo }} / DE</th>
                <template v-if="profundidadeAtiva === 'lote'">
                  <th>Fabricação</th>
                  <th>Validade</th>
                </template>
                <th class="text-end">Disponível</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(linha, idx) in linhasArvore" :key="linha.data.chave || idx">
                <tr
                  v-if="linhaTransitoVisivel(linha)"
                  :class="rowClassArvore(linha)"
                  @click="selecionarNo(linha)"
                >
                  <!-- Coluna de seleção (nó DE = checkbox; nó pai = seleção em massa) -->
                  <td class="text-center" @click.stop>
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :id="'sel-aba2-' + (linha.data.chave || 'k' + idx)"
                      :checked="isNoSelecionado(linha)"
                      @change="selecionarNo(linha)"
                    />
                  </td>
                  <!-- Coluna de expansão (chevron) -->
                  <td class="text-center" @click.stop>
                    <button
                      v-if="linha.isParent"
                      type="button"
                      class="btn btn-sm btn-link p-0 text-body"
                      @click="toggleNivel(linha.data.chave)"
                    >
                      <i class="bi" :class="transitoExpandido[linha.data.chave] ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                    </button>
                  </td>
                  <!-- Descrição do nó (indentado por nível) -->
                  <td>
                    <span :class="'nivel-indent nivel-' + Math.min(linha.level, 6)">
                      <i class="bi me-1" :class="iconeDoNivel(linha.data)"></i>

                      <!-- Nível Local -->
                      <template v-if="linha.data.tipo === 'local'">
                        <span class="label-campo">Local Estoque:</span>
                        <strong>{{ linha.data.nomeLocalEstoque }}</strong>
                        <span class="label-campo ms-2">Filial:</span>
                        {{ linha.data.nomeFilial }} <code>#{{ linha.data.idFilial }}</code>
                        <span v-if="linha.data.lstTipoLocal" :class="classeTipoLocal(linha.data.lstTipoLocal) + ' ms-2'">
                          {{ rotuloTipoLocal(linha.data.lstTipoLocal) }}
                        </span>
                      </template>

                      <!-- Nível Lote -->
                      <template v-else-if="linha.data.tipo === 'lote'">
                        <span class="label-campo">Lote:</span>
                        <strong>{{ linha.data.rotuloSelecao }}</strong>
                        <span v-if="linha.data.nomeFornecedor" class="label-campo ms-2">Fornecedor:</span>
                        <span v-if="linha.data.nomeFornecedor">{{ linha.data.nomeFornecedor }}</span>
                      </template>

                      <!-- Nível Embarque -->
                      <template v-else-if="linha.data.tipo === 'embarque'">
                        <span class="label-campo">Embarque:</span>
                        <span class="fw-semibold">{{ linha.data.numeroDocumentoTransporte || '#' + linha.data.idEmbarque }}</span>
                        <span v-if="linha.data.numeroProcesso" class="label-campo ms-2">Processo:</span>
                        <span v-if="linha.data.numeroProcesso">{{ linha.data.numeroProcesso }}</span>
                      </template>

                      <!-- Nível Container -->
                      <template v-else-if="linha.data.tipo === 'container'">
                        <span class="label-campo">Container:</span>
                        <span class="fw-semibold">{{ linha.data.numeroContainer || '#' + linha.data.idEmbarqueContainer }}</span>
                        <span v-if="linha.data.lstTipoContainer" class="label-campo ms-2">Tipo:</span>
                        <span v-if="linha.data.lstTipoContainer">{{ linha.data.lstTipoContainer }}</span>
                      </template>

                      <!-- Nível PLP -->
                      <template v-else-if="linha.data.tipo === 'plp'">
                        <span class="label-campo">PLP:</span>
                        <span class="fw-semibold">#{{ linha.data.idEmbarquePackingListProduto }}</span>
                      </template>

                      <!-- Nível DE (folha) -->
                      <template v-else>
                        <span class="label-campo">Documento de Entrada:</span>
                        <span class="fw-semibold">{{ linha.data.numeroDocumentoEntrada || '#' + linha.data.idDocumentoEntrada }}</span>
                        <span v-if="linha.data.numeroLoteFornecedor" class="label-campo ms-2">Lote:</span>
                        <span v-if="linha.data.numeroLoteFornecedor">{{ linha.data.numeroLoteFornecedor }}</span>
                        <span v-if="linha.data.nomeFornecedor" class="label-campo ms-2">Produtor:</span>
                        <span v-if="linha.data.nomeFornecedor">{{ linha.data.nomeFornecedor }}</span>
                        <span v-if="isNoSelecionado(linha)" class="badge badge-ghost-success ms-2">Selecionado</span>
                      </template>
                    </span>
                  </td>

                  <!-- Lote: Fabricação / Validade -->
                  <template v-if="profundidadeAtiva === 'lote'">
                    <td>{{ linha.data.dataFabricacao || '---' }}</td>
                    <td>{{ linha.data.dataValidade || '---' }}</td>
                  </template>

                  <!-- Quantidade -->
                  <td class="text-end fw-semibold">{{ formatarQtd(linha.data.quantidadeDisponivel) }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div v-else class="alert alert-secondary text-center">
          <i class="bi bi-info-circle me-1"></i>
          Nenhum saldo em trânsito encontrado para este produto.
        </div>
      </div>
    </div>

    <!-- Tabela de estoque selecionado (oculta para serviços) -->
    <div v-if="!isServico && totalSelecionados > 0" class="selecao-resumo mt-3">
      <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
        <span class="fw-semibold">
          <i class="bi bi-check-circle-fill text-primary me-1"></i> Estoque selecionado
        </span>
        <span :class="obterBadgeNeutro()">{{ totalSelecionados }} origem{{ totalSelecionados === 1 ? '' : 'ns' }}</span>
      </div>
      <div class="table-responsive" style="max-height: 220px; overflow-y: auto;">
        <table class="table table-sm table-bordered align-middle mb-0 tabela-selecionados">
          <thead class="table-light sticky-top">
            <tr>
              <th style="width: 34px;">#</th>
              <th>Origem (Local · Embarque · Container · PLP · DE)</th>
              <th>Filial</th>
              <th>Tipo</th>
              <th class="text-end">Disponível</th>
              <th style="width: 40px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(sel, idx) in itensSelecionados" :key="'sel-' + idx">
              <td class="text-center text-body-secondary">{{ idx + 1 }}</td>
              <td class="fw-semibold">{{ rotuloSelecaoCompleto(sel) || '-' }}</td>
              <td>{{ sel.nomeFilial || (sel.idFilial ? '#' + sel.idFilial : '-') }}</td>
              <td>
                <span v-if="sel.lstTipoLocal" :class="classeTipoLocal(sel.lstTipoLocal)">
                  {{ rotuloTipoLocal(sel.lstTipoLocal) }}
                </span>
                <span v-else>-</span>
              </td>
              <td class="text-end fw-semibold">{{ formatarQtd(sel.quantidadeDisponivel) }}</td>
              <td class="text-center">
                <button
                  type="button"
                  class="btn btn-sm btn-link text-danger p-0"
                  title="Remover seleção"
                  @click="removerSelecao(idx)"
                >
                  <i class="bi bi-x-lg"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="d-flex justify-content-end mt-2">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="limparSelecao">
          <i class="bi bi-x-lg me-1"></i> Limpar seleção
        </button>
      </div>
    </div>

    <!-- Footer: botão Selecionar -->
    <template #footer>
      <div class="d-flex gap-2 w-100 justify-content-end align-items-center">
        <button type="button" class="btn btn-outline-secondary" @click="_cancelar">
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary fw-bold"
          :disabled="!isServico && totalSelecionados === 0"
          @click="_confirmarSelecao"
        >
          <i class="bi bi-check-lg me-1"></i>
          <template v-if="isServico">Selecionar Serviço</template>
          <template v-else>Selecionar ({{ totalSelecionados }})</template>
        </button>
      </div>
    </template>
  </ModalFormularioV2>

  <ConfirmationModal
    ref="confirmacaoTrocaAbaRef"
    title="Trocar de aba?"
    message="As seleções de estoque atuais serão perdidas ao trocar de aba. Deseja continuar?"
    confirm-button-text="Sim, trocar e limpar seleções"
    cancel-button-text="Não, manter seleções"
    @confirmed="onConfirmTrocaAba"
  />
</template>

<style scoped>
/* Área de seleção demarcada */
.area-selecao {
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  padding: 0.75rem;
  background-color: var(--bs-tertiary-bg);
}

/* Seletor de modo (nav-tabs) — padrão PedidoVendaCadastro adaptado para primary */
.nav-tabs .nav-link {
  border: none;
  color: var(--bs-secondary-color);
  font-weight: 500;
  padding: 0.5rem 1rem;
  transition: all 0.2s ease;
}

.nav-tabs .nav-link:hover {
  color: var(--bs-primary-text-emphasis);
  background-color: var(--bs-primary-bg-subtle);
}

.nav-tabs .nav-link.active {
  color: var(--bs-primary-text-emphasis);
  background-color: transparent;
  border-bottom: 3px solid var(--bs-primary);
  font-weight: 600;
}

.table > tbody > tr > td {
  vertical-align: middle;
}

.table > tbody > tr:hover {
  cursor: pointer;
}

/* Guias de indentação por nível da árvore (Trânsito/Lotes) */
.nivel-indent {
  display: inline-block;
}

.nivel-1 {
  padding-left: 0;
}

.nivel-2 {
  padding-left: 1.25rem;
}

.nivel-3 {
  padding-left: 2.5rem;
}

.nivel-4 {
  padding-left: 3.75rem;
}

.nivel-5 {
  padding-left: 5rem;
}

.nivel-6 {
  padding-left: 6.25rem;
}

/* Rótulo de campo nas linhas da árvore (labels separados das informações) */
.label-campo {
  color: var(--bs-secondary-color);
  font-size: 0.78rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* Tabela de estoque selecionado */
.tabela-selecionados {
  font-size: 0.8rem;
}

.tabela-selecionados th {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--bs-secondary-color);
  background-color: var(--bs-secondary-bg);
}

/* Linha selecionada (adaptável ao modo escuro) */
.linha-selecionada {
  background-color: var(--bs-primary-bg-subtle) !important;
  box-shadow: inset 3px 0 0 0 var(--bs-primary);
}

/* Destaque dos descendentes do nó selecionado */
.linha-filha-selecionada {
  box-shadow: inset 3px 0 0 0 var(--bs-primary);
}

/* Bloco de filial (não é badge) */
.bloco-filial {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.5rem;
  border-radius: 0.375rem;
  background-color: var(--bs-secondary-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color);
  font-size: 0.8rem;
}

/* Cards de armazém (Pronta Entrega) */
.card-armazem {
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.card-armazem:hover {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.15rem var(--bs-primary-border-subtle);
}

.card-armazem-selecionado {
  border-color: var(--bs-primary) !important;
  box-shadow: 0 0 0 0.2rem var(--bs-primary-border-subtle);
  background-color: var(--bs-primary-bg-subtle);
}

/* Barra de resumo da seleção */
.selecao-resumo {
  position: sticky;
  bottom: 0;
  background-color: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
}

.badge-ghost-info {
  background-color: var(--bs-info-bg-subtle);
  color: var(--bs-info-text-emphasis);
}

.badge-ghost-secondary {
  background-color: var(--bs-secondary-bg-subtle);
  color: var(--bs-secondary-text-emphasis);
}

.badge-ghost-success {
  background-color: var(--bs-success-bg-subtle);
  color: var(--bs-success-text-emphasis);
}
</style>
